/**
 * Strategy Lab account test: a backtest's trades through account rules (personal limits or a
 * prop-firm challenge), from every week start and for each starting balance.
 * BE: `GET /account-presets`, `POST /hypotheses/{id}/account-test` (a job).
 */
import { authenticatedFetch } from "./apiClient";
import { enqueueAndPollStrategyRdJob } from "./strategyRdApi";
import { STRATEGY_RD_API_BASE } from "../types/strategyRd";
import type { StrategyRdJobProgress } from "../utils/strategyRdJobs";

export type StrategyRdAccountPreset = {
  id: string;
  name: string;
  /** True for prop-firm challenges (a phase has a profit target). */
  isChallenge: boolean;
  initialBalance: number;
  /** Full rules as the BE returns them (sent back with overrides). */
  rules: Record<string, unknown>;
};

export type StrategyRdBand = { p25: number | null; median: number | null; p75: number | null };

/** One start date's own outcome (the bands summarise these). */
export type StrategyRdAccountRun = {
  start: string | null;
  /** passed / funded / failed / stalled / killed / completed. */
  status: string;
  reason: string | null;
  passed: boolean;
  days: number | null;
  trades: number | null;
  returnPct: number | null;
  maxDrawdownPct: number | null;
  phase: string | null;
  paid: number;
  /** Days from the start to when the kill switch tripped (personal accounts). */
  killedDay: number | null;
};

export type StrategyRdAccountBalanceResult = {
  balance: number;
  runs: number;
  runList: StrategyRdAccountRun[];
  statusShare: Record<string, number>;
  skippedPerRun: Record<string, number> | null;
  challenge: {
    passRate: number;
    failRate: number;
    stalledRate: number;
    daysToPass: StrategyRdBand;
    challengesPerPass: number | null;
    failReasons: Record<string, number> | null;
    /** How far stalled runs got in the time given (%), and trades in a typical run. */
    stalledReturnPct: StrategyRdBand;
    tradesPerRun: number | null;
    targetPct: number | null;
  } | null;
  funded: {
    accounts: number;
    paid: StrategyRdBand;
    paidZeroShare: number | null;
    lostShare: number | null;
    paidPerChallengeBought: number | null;
  } | null;
  personal: {
    returnPct: StrategyRdBand & { worst: number | null; best: number | null };
    belowStartShare: number | null;
    maxDrawdownPct: { median: number | null; p75: number | null; worst: number | null };
    drawdownOver30Share: number | null;
    drawdownOver50Share: number | null;
    worstStart: string | null;
    bestStart: string | null;
  } | null;
};

export type StrategyRdAccountPhase = {
  name: string;
  status: string;
  reason: string | null;
  days: number | null;
  trades: number | null;
  returnPct: number | null;
  maxDrawdownPct: number | null;
  paid: number | null;
};

export type StrategyRdAccountResult = {
  rulesName: string;
  trades: number;
  minLot: number | null;
  /** Days of data the trades span. */
  dataDays: number | null;
  /** Runs were cut short because the backtest is shorter than the usual run length. */
  horizonShortened: boolean;
  /** The usual run length (180 days for a challenge, 365 for a personal account). */
  fullHorizonDays: number | null;
  note: string | null;
  starts: number;
  every: string;
  horizonDays: number;
  firstStart: string | null;
  lastStart: string | null;
  byBalance: StrategyRdAccountBalanceResult[];
  firstRun: { status: string; reason: string | null; phases: StrategyRdAccountPhase[]; balanceEod: [string, number][] };
};

export type StrategyRdAccountTestRequest = {
  rules?: Record<string, unknown> | null;
  balances?: number[];
  every?: "week" | "month";
  horizon_days?: number;
  costs?: "as_set" | "realistic";
  min_lot?: number;
};

const rec = (v: unknown): Record<string, unknown> =>
  v && typeof v === "object" && !Array.isArray(v) ? (v as Record<string, unknown>) : {};
const num = (v: unknown): number | null => (typeof v === "number" && Number.isFinite(v) ? v : null);
const str = (v: unknown): string | null => (typeof v === "string" && v.trim() ? v : null);
const band = (v: unknown): StrategyRdBand => {
  const r = rec(v);
  return { p25: num(r.p25), median: num(r.median), p75: num(r.p75) };
};
const numMap = (v: unknown): Record<string, number> | null => {
  const out: Record<string, number> = {};
  for (const [k, x] of Object.entries(rec(v))) if (typeof x === "number") out[k] = x;
  return Object.keys(out).length ? out : null;
};

export function parseStrategyRdAccountPresets(raw: unknown): StrategyRdAccountPreset[] {
  const list = Array.isArray(rec(raw).presets) ? (rec(raw).presets as unknown[]) : [];
  return list.map((p) => {
    const r = rec(p);
    const phases = Array.isArray(r.phases) ? (r.phases as unknown[]) : [];
    return {
      id: String(r.id ?? ""),
      name: String(r.name ?? r.id ?? ""),
      isChallenge: phases.some((ph) => num(rec(ph).target_pct) != null),
      initialBalance: num(r.initial_balance) ?? 10_000,
      rules: { ...r },
    };
  }).filter((p) => p.id);
}

export function parseStrategyRdAccountResult(raw: unknown): StrategyRdAccountResult {
  const r = rec(raw);
  const many = rec(r.many_starts);
  const one = rec(r.single_run);
  const byBalance = (Array.isArray(many.by_balance) ? (many.by_balance as unknown[]) : []).map((b) => {
    const x = rec(b);
    const ch = x.challenge ? rec(x.challenge) : null;
    const fu = x.funded ? rec(x.funded) : null;
    const pe = x.personal ? rec(x.personal) : null;
    return {
      balance: num(x.balance) ?? 0,
      runs: num(x.runs) ?? 0,
      statusShare: numMap(x.status_share) ?? {},
      skippedPerRun: numMap(x.skipped_per_run),
      runList: (Array.isArray(x.run_list) ? (x.run_list as unknown[]) : []).map((r) => {
        const y = rec(r);
        return {
          start: str(y.start),
          status: str(y.status) ?? "",
          reason: str(y.reason),
          passed: y.passed === true,
          days: num(y.days),
          trades: num(y.trades),
          returnPct: num(y.return_pct),
          maxDrawdownPct: num(y.max_drawdown_pct),
          phase: str(y.phase),
          paid: num(y.paid) ?? 0,
          killedDay: num(y.killed_day),
        };
      }),
      challenge: ch
        ? {
            passRate: num(ch.pass_rate) ?? 0,
            failRate: num(ch.fail_rate) ?? 0,
            stalledRate: num(ch.stalled_rate) ?? 0,
            daysToPass: band(ch.days_to_pass),
            challengesPerPass: num(ch.challenges_per_pass),
            failReasons: numMap(ch.fail_reasons),
            stalledReturnPct: band(ch.stalled_return_pct),
            tradesPerRun: num(ch.trades_per_run),
            targetPct: num(ch.target_pct),
          }
        : null,
      funded: fu
        ? {
            accounts: num(fu.accounts) ?? 0,
            paid: band(fu.paid),
            paidZeroShare: num(fu.paid_zero_share),
            lostShare: num(fu.lost_share),
            paidPerChallengeBought: num(fu.paid_per_challenge_bought),
          }
        : null,
      personal: pe
        ? {
            returnPct: { ...band(pe.return_pct), worst: num(rec(pe.return_pct).worst), best: num(rec(pe.return_pct).best) },
            belowStartShare: num(pe.below_start_share),
            maxDrawdownPct: {
              median: num(rec(pe.max_drawdown_pct).median),
              p75: num(rec(pe.max_drawdown_pct).p75),
              worst: num(rec(pe.max_drawdown_pct).worst),
            },
            drawdownOver30Share: num(pe.drawdown_over_30_share),
            drawdownOver50Share: num(pe.drawdown_over_50_share),
            worstStart: str(pe.worst_start),
            bestStart: str(pe.best_start),
          }
        : null,
    };
  });
  const phases = (Array.isArray(one.phases) ? (one.phases as unknown[]) : []).map((p) => {
    const x = rec(p);
    return {
      name: String(x.name ?? ""),
      status: String(x.status ?? ""),
      reason: str(x.reason),
      days: num(x.days),
      trades: num(x.trades),
      returnPct: num(x.return_pct),
      maxDrawdownPct: num(x.max_drawdown_pct),
      paid: num(x.paid),
    };
  });
  const eod = (Array.isArray(one.balance_eod) ? (one.balance_eod as unknown[]) : [])
    .map((e) => (Array.isArray(e) && typeof e[0] === "string" && typeof e[1] === "number" ? ([e[0], e[1]] as [string, number]) : null))
    .filter((e): e is [string, number] => e != null);
  return {
    rulesName: String(rec(r.rules).name ?? ""),
    trades: num(r.trades) ?? 0,
    minLot: num(r.min_lot),
    dataDays: num(r.data_days),
    horizonShortened: r.horizon_shortened === true,
    fullHorizonDays: num(r.full_horizon_days),
    note: str(r.note),
    starts: num(many.starts) ?? 0,
    every: String(many.every ?? "week"),
    horizonDays: num(many.horizon_days) ?? 0,
    firstStart: str(many.first_start),
    lastStart: str(many.last_start),
    byBalance,
    firstRun: { status: String(one.status ?? ""), reason: str(one.reason), phases, balanceEod: eod },
  };
}

export async function listStrategyRdAccountPresets(signal?: AbortSignal): Promise<StrategyRdAccountPreset[]> {
  const response = await authenticatedFetch(`${STRATEGY_RD_API_BASE}/account-presets`, { signal });
  if (!response.ok) throw new Error("Could not load account presets.");
  return parseStrategyRdAccountPresets(await response.json().catch(() => ({})));
}

export async function runStrategyRdAccountTest(
  hypothesisId: string,
  body: StrategyRdAccountTestRequest,
  signal?: AbortSignal,
  onProgress?: (progress: StrategyRdJobProgress) => void
): Promise<StrategyRdAccountResult> {
  const payload = await enqueueAndPollStrategyRdJob(
    `${STRATEGY_RD_API_BASE}/hypotheses/${encodeURIComponent(hypothesisId.trim())}/account-test`,
    body,
    { signal, maxWaitMs: 120_000, onProgress, stopOn: ["ready", "failed"], fallbackErrorMessage: "Could not run the account test." }
  );
  return parseStrategyRdAccountResult(payload);
}
